import { useCallback, useEffect, useMemo, useState } from 'react';
import type { DataTableServer } from '@/components/ui/DataTable';

export interface CursorPagerOptions {
  /** Aktueller Cursor (URL- oder Komponenten-State), undefined = erste Seite. */
  cursor: string | undefined;
  /** `links.next` aus der Antwort, null wenn es keine weitere Seite gibt. */
  nextCursor: string | null;
  /** Setzt den Cursor (navigate oder setState). */
  onCursorChange: (cursor: string | undefined) => void;
  pageSize: number;
  /**
   * Setzt die Seitengröße. Der Aufrufer muss dabei selbst auf die erste
   * Seite zurücksetzen (Cursor entfernen) - sonst zeigt die neue Größe
   * einen Ausschnitt, der zum alten Cursor gehört.
   */
  onPageSizeChange: (size: number) => void;
  /** Freitext links in der Fußzeile, z.B. "~855 Eintraege gesamt". */
  totalLabel?: string | undefined;
}

/**
 * Macht aus einer Cursor-Liste das `server`-Objekt der DataTable.
 *
 * Cursor-APIs kennen nur "vorwaerts": die Antwort enthält den Cursor der
 * nächsten Seite, nie den der vorigen. Damit "Zurueck" trotzdem geht, merkt
 * sich der Hook die besuchten Cursor auf einem Stapel. Springt der Cursor
 * von außen auf die erste Seite (Filterwechsel), ist der Stapel hinfällig
 * und wird geleert - sonst führt "Zurueck" auf eine Seite, die es unter dem
 * neuen Filter gar nicht mehr gibt.
 */
export function useCursorPager(options: CursorPagerOptions): DataTableServer {
  const { cursor, nextCursor, onCursorChange, pageSize, onPageSizeChange, totalLabel } = options;
  const [stack, setStack] = useState<Array<string | undefined>>([]);

  useEffect(() => {
    if (cursor === undefined) {
      setStack((prev) => (prev.length === 0 ? prev : []));
    }
  }, [cursor]);

  const onNext = useCallback(() => {
    if (nextCursor === null) {
      return;
    }
    setStack((prev) => [...prev, cursor]);
    onCursorChange(nextCursor);
  }, [cursor, nextCursor, onCursorChange]);

  const onPrev = useCallback(() => {
    if (stack.length === 0) {
      return;
    }
    const previous = stack[stack.length - 1];
    setStack(stack.slice(0, -1));
    onCursorChange(previous);
  }, [stack, onCursorChange]);

  const handlePageSizeChange = useCallback(
    (size: number) => {
      setStack([]);
      onPageSizeChange(size);
    },
    [onPageSizeChange]
  );

  return useMemo(
    () => ({
      pageSize,
      onPageSizeChange: handlePageSizeChange,
      hasPrev: stack.length > 0,
      onPrev,
      hasNext: nextCursor !== null,
      onNext,
      ...(totalLabel !== undefined ? { totalLabel } : {}),
    }),
    [pageSize, handlePageSizeChange, stack.length, onPrev, nextCursor, onNext, totalLabel]
  );
}
